import api from './api';

class FlightSearchService {
  // Flight times are stored in UTC and displayed in the viewer's local zone, so
  // time-of-day filtering has to be bucketed in that same zone server-side.
  getViewerTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      return '';
    }
  }

  normalizeTimeOfDay(timeOfDay) {
    if (Array.isArray(timeOfDay)) {
      return timeOfDay
        .map((slot) => String(slot).trim().toLowerCase())
        .filter(Boolean);
    }

    if (typeof timeOfDay === 'string') {
      return timeOfDay
        .split(',')
        .map((slot) => slot.trim().toLowerCase())
        .filter(Boolean);
    }

    return [];
  }

  // Search flights using the backend API
  async searchFlights(searchParams) {
    try {
      console.log('🔍 FlightSearchService: Searching flights with params:', searchParams);
      console.log('🔍 FlightSearchService: Trip type:', searchParams.tripType);

      // For multi-city, use POST endpoint with segments
      const isMultiCity = searchParams.tripType && searchParams.tripType.toLowerCase().includes('multi');
      if (isMultiCity && searchParams.segments) {
        return await this.searchMultiCityFlights(searchParams);
      }

      // For one-way and return flights, use GET endpoint
      const backendParams = this.transformSearchParams(searchParams);

      // Add booking_token to backend parameters if present
      if (searchParams.booking_token) {
        backendParams.booking_token = searchParams.booking_token;
      }
      if (searchParams.change_legs?.length) {
        backendParams.change_legs = Array.isArray(searchParams.change_legs)
          ? searchParams.change_legs.join(',')
          : searchParams.change_legs;
      }

      // For development, if backend is not available, return mock data
      try {
        console.log('🔍 FlightSearchService: Making API call to /flights/search');
        const response = await api.get('/flights/search', { params: backendParams });

        console.log('✅ FlightSearchService: API Response:', response);

        // The response interceptor returns response.data, so check if it has success property
        if (response && (response.success || response.data)) {
          console.log('🔍 FlightSearchService: Transforming standard search results');
          return this.transformSearchResults(response);
        } else {
          throw new Error(response?.message || 'Failed to search flights');
        }
      } catch (apiError) {
        console.error('❌ FlightSearchService: API Error:', apiError);

        // If backend is not available, return mock data for development
        if (apiError.code === 'ECONNREFUSED' || apiError.message.includes('Network Error') || apiError.message.includes('ECONNREFUSED')) {
          console.log('⚠️ FlightSearchService: Backend not available, returning mock data');
          return this.getMockSearchResults(searchParams);
        }
        throw apiError;
      }
    } catch (error) {
      console.error('❌ FlightSearchService: Search error:', error);
      throw error;
    }
  }

  // Search multi-city flights using POST endpoint
  async searchMultiCityFlights(searchParams) {
    try {
      console.log('🔍 searchMultiCityFlights: Input params:', searchParams);

      const { segments, passengers, booking_token, time_of_day } = searchParams;

      // Validate segments
      if (!segments || segments.length < 2) {
        throw new Error('Multi-city search requires at least 2 segments');
      }

      // Transform segments to backend format
      // Backend expects airport IDs as strings
      const backendSegments = segments.map(segment => ({
        from: String(segment.from),
        to: String(segment.to),
        date: this.formatDateForAPI(segment.date)
      }));

      const requestBody = {
        segments: backendSegments,
        passengers: passengers || 1,
        booking_token
      };

      if (searchParams.change_legs?.length) {
        requestBody.change_legs = Array.isArray(searchParams.change_legs)
          ? searchParams.change_legs
          : String(searchParams.change_legs).split(',').map((t) => t.trim()).filter(Boolean);
      }

      const timeOfDaySlots = this.normalizeTimeOfDay(time_of_day);
      if (timeOfDaySlots.length > 0) {
        requestBody.time_of_day = timeOfDaySlots;
        const viewerTz = this.getViewerTimeZone();
        if (viewerTz) {
          requestBody.tz = viewerTz;
        }
      }

      console.log('🔍 searchMultiCityFlights: Making POST request to /flights/search/multi-city');
      console.log('🔍 searchMultiCityFlights: Request body:', requestBody);

      const response = await api.post('/flights/search/multi-city', requestBody);

      console.log('✅ searchMultiCityFlights: API Response:', response);

      if (response && (response.success || response.data)) {
        return this.transformMultiCityResults(response);
      } else {
        throw new Error(response?.message || 'Failed to search multi-city flights');
      }
    } catch (error) {
      console.error('❌ searchMultiCityFlights: Error:', error);
      throw error;
    }
  }

  // Search return flights after outbound flight selection
  async searchReturnFlights(searchParams) {
    try {
      console.log('🔍 searchReturnFlights: Input params:', searchParams);

      const {
        departure_airport,
        arrival_airport,
        return_date,
        departure_date,
        outbound_flight_id,
        passengers = 1,
        time_of_day,
        page = 1
      } = searchParams;

      const returnDate = return_date || departure_date;

      // Validate required parameters
      if (!departure_airport || !arrival_airport || !returnDate || !outbound_flight_id) {
        throw new Error('Departure airport, arrival airport, return date, and outbound flight ID are required');
      }

      const backendParams = {
        departure_airport: String(departure_airport),
        arrival_airport: String(arrival_airport),
        return_date: this.formatDateForAPI(returnDate),
        outbound_flight_id: outbound_flight_id,  // Pass flight ID
        passengers: passengers || 1,
        page: page || 1
      };

      const timeOfDaySlots = this.normalizeTimeOfDay(time_of_day);
      if (timeOfDaySlots.length > 0) {
        backendParams.time_of_day = timeOfDaySlots.join(',');
        const viewerTz = this.getViewerTimeZone();
        if (viewerTz) {
          backendParams.tz = viewerTz;
        }
      }

      console.log('🔍 searchReturnFlights: Making GET request to /flights/search/return');
      console.log('🔍 searchReturnFlights: Params:', backendParams);

      const response = await api.get('/flights/search/return', { params: backendParams });

      console.log('✅ searchReturnFlights: API Response:', response);

      if (response && (response.success || response.data)) {
        return this.transformReturnFlightsResults(response);
      } else {
        throw new Error(response?.message || 'Failed to search return flights');
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to search return flights';
      console.error('❌ searchReturnFlights: Error:', message, error);
      throw new Error(message);
    }
  }

  // Transform multi-city search results
  transformMultiCityResults(backendResponse) {
    const data = backendResponse?.data || backendResponse;

    if (!data) {
      return { success: true, segments: [], totalSegments: 0 };
    }

    // Check if response has segments (multi-city response format)
    if (data.segments && Array.isArray(data.segments)) {
      const segments = data.segments.map(segment => {
        // Get airport codes from the first flight if available
        const firstFlight = segment.flights && segment.flights.length > 0 ? segment.flights[0] : null;
        const fromCode = firstFlight?.departure?.airport_code || segment.from;
        const toCode = firstFlight?.arrival?.airport_code || segment.to;

        return {
          segmentNumber: segment.segmentNumber || segment.segment_number,
          from: fromCode,
          to: toCode,
          date: segment.date || segment.departure_date,
          flights: (segment.flights || []).map(flight => this.transformFlightData(flight)),
          totalFlights: segment.totalFlights || segment.flights?.length || 0
        };
      });

      const result = {
        success: true,
        segments,
        totalSegments: data.totalSegments || data.segments.length,
        passengers: data.passengers
      };

      console.log('✅ transformMultiCityResults: Transformed result:', result);
      return result;
    }

    // If response doesn't have segments structure, check if it's a standard response
    // and convert it to multi-city format
    if (data.outbound || data.return) {
      console.log('🔍 transformMultiCityResults: Converting standard response to multi-city format');

      // For multi-city, we might get outbound/return format, convert to segments
      const segments = [];

      if (data.outbound) {
        segments.push({
          segmentNumber: 1,
          from: data.outbound[0]?.departure?.airport_code || 'DEP',
          to: data.outbound[0]?.arrival?.airport_code || 'ARR',
          date: data.outbound[0]?.departure?.datetime,
          flights: (data.outbound || []).map(flight => this.transformFlightData(flight)),
          totalFlights: data.outbound?.length || 0
        });
      }

      if (data.return) {
        segments.push({
          segmentNumber: 2,
          from: data.return[0]?.departure?.airport_code || 'DEP',
          to: data.return[0]?.arrival?.airport_code || 'ARR',
          date: data.return[0]?.departure?.datetime,
          flights: (data.return || []).map(flight => this.transformFlightData(flight)),
          totalFlights: data.return?.length || 0
        });
      }

      const result = {
        success: true,
        segments,
        totalSegments: segments.length,
        passengers: data.passengers
      };

      console.log('✅ transformMultiCityResults: Converted result:', result);
      return result;
    }

    // If response doesn't have segments structure, return empty
    console.warn('⚠️ transformMultiCityResults: Could not find segments or outbound/return in response');
    return { success: true, segments: [], totalSegments: 0 };
  }

  // Transform return flights search results
  transformReturnFlightsResults(backendResponse) {
    console.log('🔍 transformReturnFlightsResults: Input response:', backendResponse);

    if (!backendResponse?.data) {
      console.warn('⚠️ transformReturnFlightsResults: No data in response');
      return { success: true, returnFlights: [], pagination: {} };
    }

    const returnFlights = (backendResponse.data.return || []).map(
      flight => this.transformFlightData(flight)
    );

    const result = {
      success: true,
      returnFlights,
      pagination: backendResponse.data.pagination
    };

    console.log('✅ transformReturnFlightsResults: Transformed result:', result);
    return result;
  }

  // Get flight details by ID
  async getFlightDetails(flightId) {
    try {
      const response = await api.get(`/flights/details/${flightId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get airports list
  async getAirports(searchParams = {}) {
    try {
      const response = await api.get('/flights/airports', { params: searchParams });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Transform search parameters from frontend format to backend format
  transformSearchParams(frontendParams) {
    console.log('🔍 transformSearchParams: Input params:', frontendParams);

    const {
      from,
      to,
      departure,
      return: returnDate,
      passengers,
      tripType,
      nearby,
      time_of_day
    } = frontendParams;

    // Map trip types to backend format
    const routeTypeMap = {
      'One Way': 'One Way',
      'Returns': 'Round Trip',
      'Multicity': 'Multicity'
    };

    // For one-way and return flights
    const backendParams = {
      departure_airport: from,
      arrival_airport: to,
      departure_date: this.formatDateForAPI(departure),
      passengers: passengers || 1,
      route_type: routeTypeMap[tripType] || 'One Way',
      page: frontendParams.page || 1
    };

    // Add return date for round trip
    if (returnDate && (tripType === 'Returns' || tripType === 'Round Trip')) {
      backendParams.return_date = this.formatDateForAPI(returnDate);
    }

    // Add nearby airports flag
    if (nearby) {
      backendParams.nearby_airport = true;
    }

    const timeOfDaySlots = this.normalizeTimeOfDay(time_of_day);
    if (timeOfDaySlots.length > 0) {
      backendParams.time_of_day = timeOfDaySlots.join(',');
      const viewerTz = this.getViewerTimeZone();
      if (viewerTz) {
        backendParams.tz = viewerTz;
      }
    }

    console.log('✅ transformSearchParams: Backend params:', backendParams);
    return backendParams;
  }

  // Format date for API (convert from various frontend formats to YYYY-MM-DD)
  formatDateForAPI(dateString) {
    if (!dateString) return null;

    // If already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Try to parse various date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString().split('T')[0];
  }

  // Transform backend flight data to frontend format
  transformFlightData(backendFlight) {
    if (!backendFlight) return null;

    console.log('🔍 transformFlightData: Input flight:', backendFlight);
    console.log('🔍 transformFlightData: aircraft_images:', backendFlight.aircraft_images);
    console.log('🔍 transformFlightData: is_bel_flight:', backendFlight.is_bel_flight);
    console.log('🔍 transformFlightData: display_price:', backendFlight.display_price);

    // Determine if this is a BEL flight
    const isBEL = backendFlight.is_bel_flight || (backendFlight.block_empty_price && parseFloat(backendFlight.block_empty_price) > 0);
    
    const rawDisplayPrice = parseFloat(backendFlight.display_price) || 0;
    const seatPrice = parseFloat(backendFlight.seat_price) || 0;
    const blockEmptyPrice = parseFloat(backendFlight.block_empty_price) || 0;
    const commissionFee = parseFloat(backendFlight.commission_fee) || 0;
    const applyCommission = (base) => {
      const amount = parseFloat(base) || 0;
      return commissionFee > 0
        ? Math.round(amount + (amount * commissionFee) / 100)
        : Math.round(amount);
    };

    // display_price = Fare/Pax from API; price = legacy field for list cards (commission-inclusive total per pax)
    let price = 0;
    if (isBEL) {
      price =
        rawDisplayPrice > 0
          ? rawDisplayPrice
          : applyCommission(blockEmptyPrice);
    } else if (seatPrice) {
      price =
        rawDisplayPrice > 0
          ? rawDisplayPrice
          : applyCommission(seatPrice);
    }

    const transformed = {
      id: backendFlight.id,
      flightNumber: backendFlight.flight_number,
      // Preserve raw airport IDs for return flight fetching
      departure_airport: backendFlight.departure_airport,
      arrival_airport: backendFlight.arrival_airport,
      departure_date_time: backendFlight.departure_date_time,
      arrival_date_time: backendFlight.arrival_date_time,
      aircraft: {
        name: backendFlight.aircraft?.aircraftmodel?.model || 'Aircraft',
        registration: backendFlight.registration_number || backendFlight.aircraft?.registration_number,
        type: backendFlight.aircraft?.aircraftmodel?.type_designator,
        capacity: backendFlight.aircraft?.seating_capacity
      },
      departure: {
        airport: backendFlight.departure?.airport_name,
        airportCode: backendFlight.departure?.airport_code,
        airport_code: backendFlight.departure?.airport_code,
        city: backendFlight.departure?.airport_name,
        datetime: backendFlight.departure?.datetime || backendFlight.departure_date_time,
        terminal: backendFlight.terminal,
        gate: backendFlight.gate
      },
      arrival: {
        airport: backendFlight.arrival?.airport_name,
        airportCode: backendFlight.arrival?.airport_code,
        airport_code: backendFlight.arrival?.airport_code,
        city: backendFlight.arrival?.airport_name,
        datetime: backendFlight.arrival?.datetime || backendFlight.arrival_date_time
      },
      duration: this.calculateDurationMinutes(backendFlight.flight_time),
      totalSeats: backendFlight.seat_total,
      remainingSeat: backendFlight.seat_available, // Alias for legacy/hook compatibility
      availableSeats: backendFlight.remainingSeats || backendFlight.seat_available,
      price,
      display_price: rawDisplayPrice > 0 ? rawDisplayPrice : price,
      display_total_fare: backendFlight.display_total_fare != null
        ? parseFloat(backendFlight.display_total_fare)
        : undefined,
      seat_price: backendFlight.seat_price,
      block_price: backendFlight.block_price,
      block_empty_price: backendFlight.block_empty_price,
      commission_fee: backendFlight.commission_fee,
      is_bel_flight: isBEL,
      flight_type: isBEL ? 'BEL' : 'Regular',
      currency: backendFlight.currency || 'USD',
      status: backendFlight.status,
      aircraft_images: backendFlight.aircraft_images || []
    };

    console.log('✅ transformFlightData: Transformed flight:', transformed);
    console.log('✅ transformFlightData: aircraft_images in result:', transformed.aircraft_images);
    console.log('✅ transformFlightData: Final price:', transformed.price, 'is_bel_flight:', transformed.is_bel_flight);

    return transformed;
  }

  // Calculate duration in minutes from flight_time string (e.g., "4:00")
  calculateDurationMinutes(flightTime) {
    if (!flightTime) return 0;

    const parts = flightTime.split(':');
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;

    return (hours * 60) + minutes;
  }

  // Transform search results from backend to frontend format
  transformSearchResults(backendResponse) {
    console.log('🔍 transformSearchResults: Input response:', backendResponse);

    if (!backendResponse?.data) {
      console.warn('⚠️ transformSearchResults: No data in response');
      return { success: true, flights: [], outboundFlights: [], returnFlights: [], total: 0 };
    }

    const outboundFlights = (backendResponse.data.outbound || []).map(
      flight => this.transformFlightData(flight)
    );

    const returnFlights = (backendResponse.data.return || []).map(
      flight => this.transformFlightData(flight)
    );

    const result = {
      success: true,
      outboundFlights,
      returnFlights,
      routeType: backendResponse.data.route_type,
      searchParams: backendResponse.data.search_params,
      totalOutbound: backendResponse.data.pagination?.total || outboundFlights.length,
      totalReturn: returnFlights.length,
      pagination: backendResponse.data.pagination
    };

    console.log('✅ transformSearchResults: Transformed result:', result);
    return result;
  }

  // Format time from datetime string
  formatTime(dateTimeString) {
    if (!dateTimeString) return '';

    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '';
    }
  }

  // Format date from datetime string
  formatDate(dateTimeString) {
    if (!dateTimeString) return '';

    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  // Calculate flight duration in hours and minutes
  calculateDuration(durationMinutes) {
    if (!durationMinutes) return '0h 0m';

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    return `${hours}h ${minutes}m`;
  }

  // Format currency
  formatCurrency(amount, currency = 'USD') {
    if (!amount) return '$0.00';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  }

  // Mock data for development when backend is not available
  getMockSearchResults(searchParams) {
    const { departure, tripType } = searchParams;

    const mockOutboundFlights = [
      {
        id: 'mock-1',
        flightNumber: 'JS101',
        aircraft: {
          name: 'Boeing 737-800',
          registration: 'N737JS',
          type: 'Commercial',
          capacity: 180
        },
        departure: {
          airport: 'John F. Kennedy International Airport',
          airportCode: 'JFK',
          city: 'New York',
          datetime: `${departure}T08:00:00Z`,
          terminal: 'T4',
          gate: 'A12'
        },
        arrival: {
          airport: 'Los Angeles International Airport',
          airportCode: 'LAX',
          city: 'Los Angeles',
          datetime: `${departure}T14:30:00Z`
        },
        duration: 390, // 6.5 hours in minutes
        totalSeats: 180,
        availableSeats: 45,
        price: 299.99,
        currency: 'USD',
        status: 'Scheduled'
      },
      {
        id: 'mock-2',
        flightNumber: 'JS205',
        aircraft: {
          name: 'Airbus A320',
          registration: 'N320JS',
          type: 'Commercial',
          capacity: 150
        },
        departure: {
          airport: 'John F. Kennedy International Airport',
          airportCode: 'JFK',
          city: 'New York',
          datetime: `${departure}T12:15:00Z`,
          terminal: 'T4',
          gate: 'B8'
        },
        arrival: {
          airport: 'Los Angeles International Airport',
          airportCode: 'LAX',
          city: 'Los Angeles',
          datetime: `${departure}T18:45:00Z`
        },
        duration: 390,
        totalSeats: 150,
        availableSeats: 23,
        price: 349.99,
        currency: 'USD',
        status: 'Scheduled'
      }
    ];

    const mockReturnFlights = tripType === 'Returns' ? [
      {
        id: 'mock-return-1',
        flightNumber: 'JS102',
        aircraft: {
          name: 'Boeing 737-800',
          registration: 'N737JS',
          type: 'Commercial',
          capacity: 180
        },
        departure: {
          airport: 'Los Angeles International Airport',
          airportCode: 'LAX',
          city: 'Los Angeles',
          datetime: `${departure}T09:00:00Z`,
          terminal: 'T1',
          gate: 'C15'
        },
        arrival: {
          airport: 'John F. Kennedy International Airport',
          airportCode: 'JFK',
          city: 'New York',
          datetime: `${departure}T17:30:00Z`
        },
        duration: 330, // 5.5 hours
        totalSeats: 180,
        availableSeats: 38,
        price: 319.99,
        currency: 'USD',
        status: 'Scheduled'
      }
    ] : [];

    return {
      success: true,
      outboundFlights: mockOutboundFlights,
      returnFlights: mockReturnFlights,
      routeType: tripType,
      searchParams: searchParams,
      totalOutbound: mockOutboundFlights.length,
      totalReturn: mockReturnFlights.length
    };
  }
}

const flightSearchService = new FlightSearchService();
export default flightSearchService;